import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableText, optionalText } from '../common.js';
import { FINANCING_KINDS, FINANCING_OBJECT_KINDS } from './financing.js';
/**
 * Finanzierungsvorhaben (KON-256): Ein geplanter Kredit, Ratenkauf oder Leasingvertrag, bevor er abgeschlossen ist. Wirkia prüft daran die Tragfähigkeit, erzeugt die Mappe für Bank
 * oder Leasinggesellschaft und legt aus einem bewilligten Vorhaben auf Wunsch den Vertrag mit Zahlungsplan an. Alle Beträge in Cent.
 */
export const PROJECT_STATUSES = ['planned', 'submitted', 'approved', 'rejected'];
export const PROJECT_STATUS_LABELS = { planned: 'Geplant', submitted: 'Eingereicht', approved: 'Bewilligt', rejected: 'Abgelehnt' };
/** Wohin der Status wechseln darf. Ein Vorhaben, aus dem schon ein Vertrag entstand, bleibt bewilligt. */
export const PROJECT_TRANSITIONS = {
    planned: ['submitted'],
    submitted: ['approved', 'rejected', 'planned'],
    approved: ['submitted'],
    rejected: ['planned'],
};
export const canMoveProject = (from, to) => PROJECT_TRANSITIONS[from].includes(to);
const cents = (label) => z.number(`Bitte ${label} als Zahl angeben`).int(`Bitte ${label} in ganzen Cent angeben`).min(0, `${label} darf nicht negativ sein`).max(100_000_000_000, `${label} ist zu hoch`);
const isoDate = z.iso.date('Bitte ein Datum angeben');
function projectFields(forCreate) {
    const d = (schema, value) => (forCreate ? schema.default(value) : schema.optional());
    return {
        kind: z.enum(FINANCING_KINDS, 'Bitte die Art wählen'),
        title: z.string('Bitte eine Bezeichnung angeben').trim().min(2, 'Bitte eine Bezeichnung angeben').max(150),
        /** Bank, Leasinggesellschaft oder Händler, bei dem das Vorhaben beantragt wird */
        lenderName: nullableText(150),
        lenderContactId: z.uuid('Ungültiger Kontakt').nullable().optional(),
        objectKind: d(z.enum(FINANCING_OBJECT_KINDS, 'Bitte den Gegenstand wählen'), 'vehicle'),
        vehicleId: z.uuid('Ungültiges Fahrzeug').nullable().optional(),
        objectDescription: nullableText(200),
        /** Kaufpreis oder Wert des Gegenstands, netto, zur Information in der Mappe */
        objectValueCents: cents('den Wert').nullable().optional(),
        plannedStart: isoDate.nullable().optional(),
        termMonths: z.number('Bitte die Laufzeit in Monaten angeben').int('Bitte ganze Monate angeben').min(1, 'Mindestens 1 Monat').max(360, 'Höchstens 360 Monate'),
        principalCents: cents('den Darlehensbetrag').nullable().optional(),
        downPaymentCents: d(cents('die Sonderzahlung'), 0),
        annualRateBp: z.number('Bitte den Zinssatz als Zahl angeben').int().min(0, 'Der Zinssatz darf nicht negativ sein').max(3000, 'Höchstens 30 % Zinsen').nullable().optional(),
        installmentCents: cents('die Rate').nullable().optional(),
        balloonCents: d(cents('die Schlussrate'), 0),
        feesCents: d(cents('die Gebühren'), 0),
        taxRate: d(z.union([z.literal(0), z.literal(7), z.literal(19)], 'Bitte 0, 7 oder 19 % wählen'), 19),
        mileagePerYearKm: z.number().int().min(0).max(1_000_000).nullable().optional(),
        excessKmCents: z.number().int().min(0).max(10_000).nullable().optional(),
        /** Wofür das Geld gebraucht wird und warum es sich lohnt, ein Absatz für die Mappe */
        purpose: nullableText(2000),
        note: nullableText(2000),
    };
}
const planRules = (v, ctx) => {
    if (v.kind === 'lease' && !v.installmentCents)
        ctx.addIssue({ code: 'custom', path: ['installmentCents'], message: 'Bei Leasing gehört die monatliche Rate zum Vorhaben' });
    if (v.kind !== 'lease' && !v.principalCents)
        ctx.addIssue({ code: 'custom', path: ['principalCents'], message: 'Bitte den Darlehensbetrag oder Kaufpreis angeben' });
    if (v.kind !== 'lease' && v.annualRateBp == null && !v.installmentCents)
        ctx.addIssue({ code: 'custom', path: ['annualRateBp'], message: 'Bitte den Zinssatz oder die monatliche Rate angeben' });
};
export const createProjectInput = z.object(projectFields(true)).strict().superRefine(planRules);
export const updateProjectInput = z
    .object({ ...Object.fromEntries(Object.entries(projectFields(false)).map(([k, v]) => [k, v.optional()])) })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Status ändern: Beim Einreichen und Entscheiden lassen sich Datum und Vermerk angeben */
export const projectStatusInput = z
    .object({
    status: z.enum(PROJECT_STATUSES, 'Bitte einen Status wählen'),
    on: isoDate.optional(),
    note: nullableText(1000),
})
    .strict();
/** Aus einem bewilligten Vorhaben den Vertrag anlegen: Der Beginn (erste Rate) ist Pflicht, Geldgeber und Vertragsnummer lassen sich ergänzen */
export const convertProjectInput = z
    .object({
    startDate: isoDate,
    lenderName: z.string().trim().min(1).max(150).optional(),
    contractNumber: nullableText(100),
    directDebit: z.boolean().optional(),
})
    .strict();
export const listProjectsQuery = paginationQuery.extend({
    q: optionalText(100),
    status: z.enum(PROJECT_STATUSES).optional(),
});
/** Hinweis, der in der Mappe und in der Oberfläche steht: Was die Zahlen sind und was nicht */
export const PROJECT_REPORT_DISCLAIMER = 'Die Zahlen stammen aus den Rechnungen, Eingangsrechnungen, Vergütungen und Verträgen, die das Unternehmen in Wirkia erfasst hat, und sind nicht geprüft. Es ist weder eine betriebswirtschaftliche Auswertung (BWA) noch ein Jahresabschluss. ' +
    'Nicht enthalten sind Steuern, Privatentnahmen und alles, was nicht in Wirkia erfasst ist. Die Mappe ergänzt die Unterlagen des Steuerberaters und ersetzt sie nicht.';
//# sourceMappingURL=projects.js.map